import { memo, useCallback, useContext, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TabButton from 'LuxKit/Tabs/TabButton'
import usePopularAirports from 'hooks/Flights/usePopularAirports'
import useDefaultAirport from 'hooks/useDefaultAirport'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import PopularFlightCard from './PopularFlightCard'
import { buildAirportCountryMap, categoriseDestinationsByCountry, isSameCityRoute, normalizeAirportCode } from 'lib/flights/popularFlightUtils'
import usePopularFlightPrices from './usePopularFlightPrices'
import useDestinationImages from './useDestinationImages'
import { buildStandaloneFlightSearchUrl } from 'lib/flights/flightUtils'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'

type TabType = 'international' | 'domestic'

const MAX_POPULAR_DESTINATIONS = 6
const EMPTY_DESTINATIONS: Array<App.AirportLocation> = []

const CarouselCardWrapper = styled.div`
  width: ${rem(300)};
  height: 100%;
`

const SectionContainer = styled.div`
  padding: ${rem(48)} 0;
`

const EMPTY_EXCLUDED = new Set<string>()

interface Props {
  excludedDestinations?: Set<string>
}

function PopularFlightsSection(props: Props) {
  const {
    excludedDestinations = EMPTY_EXCLUDED,
  } = props
  const intl = useIntl()
  const [activeTab, setActiveTab] = useState<TabType>('international')
  const defaultAirport = useDefaultAirport()
  const popularDestinations = usePopularAirports('arrival')
  const { geoCountryCode } = useContext(GeoContext)
  const allAirports = useAppSelector(state => state.geo.airports)

  const handleInternationalClick = useCallback(() => {
    setActiveTab('international')
  }, [])

  const handleDomesticClick = useCallback(() => {
    setActiveTab('domestic')
  }, [])

  const rawOriginCode = defaultAirport?.code ?? ''
  const originCode = normalizeAirportCode(rawOriginCode)
  const originName = defaultAirport?.name ?? ''
  const originCountry = defaultAirport?.countryCode ?? geoCountryCode

  const departureAirports = usePopularAirports('departure')
  const originCityName = useMemo(() => {
    const found = departureAirports.find(
      a => a.airportCode === rawOriginCode || a.airportCode === originCode,
    )
    return found?.cityDesc ?? found?.airportName ?? originName
  }, [departureAirports, rawOriginCode, originCode, originName])

  const filteredDestinations = useMemo(
    () => popularDestinations
      .filter(dest => !isSameCityRoute(originCode, dest))
      .filter(dest => !excludedDestinations.has(dest.airportCode.toUpperCase()))
      .slice(0, MAX_POPULAR_DESTINATIONS),
    [popularDestinations, originCode, excludedDestinations],
  )

  const airportCountryMap = useMemo(
    () => buildAirportCountryMap(allAirports),
    [allAirports],
  )

  const { international: internationalDestinations, domestic: domesticDestinations } = useMemo(
    () => categoriseDestinationsByCountry(filteredDestinations, originCountry, airportCountryMap),
    [filteredDestinations, originCountry, airportCountryMap],
  )

  const hasInternational = internationalDestinations.length > 0
  const hasDomestic = domesticDestinations.length > 0
  const hasBothTabs = hasInternational && hasDomestic
  const hasData = !!defaultAirport && (hasInternational || hasDomestic)

  const [inViewRef, isInView] = useInView({ triggerOnce: true })

  const destinations = useMemo(() => {
    if (hasBothTabs) {
      return activeTab === 'international' ? internationalDestinations : domesticDestinations
    }
    if (hasInternational) return internationalDestinations
    if (hasDomestic) return domesticDestinations
    return []
  }, [activeTab, hasBothTabs, hasInternational, hasDomestic, internationalDestinations, domesticDestinations])

  const priceOriginCode = originCode || undefined
  const priceData = usePopularFlightPrices(priceOriginCode, filteredDestinations)

  const visibleDestinations = useMemo(
    () => destinations.filter(dest => {
      const d = priceData[dest.airportCode]
      return (d?.loading ?? true) || !!d?.cheapest
    }),
    [destinations, priceData],
  )

  const isEnabled = !!useOptimizelyExperiment(
    OptimizelyExperiments.croPopularFlightsHomePage,
    hasData && isInView && !!visibleDestinations.length,
  )

  const destinationImages = useDestinationImages(isEnabled ? filteredDestinations : EMPTY_DESTINATIONS)

  if (!isEnabled || visibleDestinations.length === 0) {
    return <div ref={inViewRef} />
  }

  return (
    <SectionContainer ref={inViewRef}>
      <LayoutContainer>
        <VerticalSpacer gap={24}>
          <VerticalSpacer gap={4}>
            <Heading variant="heading3">
              {intl.formatMessage({
                id: 'i6RneZ88JlwXIOHIi5Dj',
                defaultMessage: 'Popular flights for you',
                description: 'Heading for the popular flights section on the flights landing page',
              })}
            </Heading>
            <BodyText variant="medium" colour="neutral-three">
              {intl.formatMessage({
                id: 'Un6NNvgwa7v0nfULsqiF',
                defaultMessage: 'Find deals on domestic and international flights',
                description: 'Subtitle for the popular flights section on the flights landing page',
              })}
            </BodyText>
          </VerticalSpacer>

          {hasBothTabs && (
            <TabButtonsGroup>
              <TabButton
                size="medium"
                isActive={activeTab === 'international'}
                onClick={handleInternationalClick}
              >
                {intl.formatMessage({
                  id: '6NCAGz5wBIMiwWSHyR6C',
                  defaultMessage: 'International',
                  description: 'Tab label for international popular flights in the flights flow',
                })}
              </TabButton>
              <TabButton
                size="medium"
                isActive={activeTab === 'domestic'}
                onClick={handleDomesticClick}
              >
                {intl.formatMessage({
                  id: 'IxjpGDAtnlRnAhRPwR_B',
                  defaultMessage: 'Domestic',
                  description: 'Tab label for domestic popular flights in the flight search or booking flow',
                })}
              </TabButton>
            </TabButtonsGroup>
          )}

          <CardCarousel snap="start" pageSize="auto">
            {visibleDestinations.map(dest => {
              const destPrice = priceData[dest.airportCode]
              const cheapest = destPrice?.cheapest
              const isLoading = destPrice?.loading ?? true
              const destCityName = dest.cityDesc ?? dest.airportName
              let searchUrl: string
              if (cheapest?.departDate) {
                searchUrl = buildStandaloneFlightSearchUrl({
                  originAirportCode: originCode,
                  originAirportName: originName,
                  destinationAirportCode: dest.airportCode,
                  destinationAirportName: dest.cityDesc ?? dest.airportName,
                  departDate: cheapest.departDate,
                  returnDate: cheapest.returnDate,
                  adults: 1,
                  children: 0,
                  infants: 0,
                  fareClass: 'economy',
                  fareType: 'return',
                })
              } else {
                searchUrl = `/flights?departureAirportCode=${originCode}&arrivalAirportCode=${dest.airportCode}`
              }
              return (
                <CarouselCardWrapper key={dest.airportCode}>
                  <PopularFlightCard
                    originCode={originCode}
                    originCityName={originCityName}
                    destinationCode={dest.airportCode}
                    destinationCityName={destCityName}
                    to={searchUrl}
                    price={cheapest?.price}
                    departDate={cheapest?.departDate}
                    returnDate={cheapest?.returnDate}
                    loading={isLoading}
                    imageId={destinationImages[dest.airportCode]}
                  />
                </CarouselCardWrapper>
              )
            })}
          </CardCarousel>

        </VerticalSpacer>
      </LayoutContainer>
    </SectionContainer>
  )
}

export default memo(PopularFlightsSection)
